#ws-chat-panel {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 450px;
  /* Height scales with the viewport (matches the old widget's 80vh) so the
     panel grows on tall screens instead of staying a fixed 600px box. The
     max-height keeps a 24px gutter top+bottom (bottom:24px above). */
  height: 80vh;
  max-height: calc(100vh - 48px);
  z-index: 99999999;
  display: none;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  /* Explicit rather than `inherit`: this is the widget's outermost shadow
     element, and `inherit` here reaches past the shadow boundary to the host
     page's font. :host now sets --ws-font too, but this makes the intent
     unmissable — never restore `inherit` on this rule. */
  font-family: var(--ws-font);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

#ws-chat-panel[open] {
  display: flex;
}

#ws-chat-panel button {
  /* Buttons don't inherit font-family from an ancestor by default (UA
     stylesheet), so this re-asserts the tenant font rather than hard-coding
     one winery's. */
  font-family: var(--ws-font);
}

.ws-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--ws-background);
  color: var(--ws-text);
}

/* Old .overlay-effect on reloadChat() — white veil + spinner (~1s). */
.ws-reload-overlay {
  position: absolute;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  animation: ws-reload-fade 0.9s ease-out forwards;
  pointer-events: none;
}

.ws-reload-overlay::before {
  content: "";
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #333;
  animation: ws-reload-spin 1s linear infinite;
}

@keyframes ws-reload-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ws-reload-fade {
  0% {
    opacity: 0;
    transform: scale(1.05);
  }
  50% {
    opacity: 0.5;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
}

.ws-header {
  /* header_background_color config key → --ws-header-bg; falls back to the
     winery's primary when unset (so existing wineries are unaffected). */
  background: var(--ws-header-bg, var(--ws-primary));
  color: #ffffff;
  height: 60px;
  flex-shrink: 0;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ws-header-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Logo height defaults to 32px (old .header-logo img); per-winery override via
   the `header_logo_height` config key → --ws-header-logo-height (set in init.js).
   width:auto preserves aspect (old fixed 130px could squash). */
.ws-header-logo img {
  height: var(--ws-header-logo-height, 32px);
  width: auto;
  max-width: 150px;
  object-fit: contain;
}

.ws-header-logo span {
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-header-logo-color);
}

.ws-header-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.ws-btn-refresh,
.ws-btn-minimize,
.ws-btn-close {
  background: transparent;
  border: none;
  color: var(--ws-header-logo-color);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.ws-btn-refresh:hover,
.ws-btn-minimize:hover,
.ws-btn-close:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* Fogarty-only header extras: login dropdown + cart icon/badge. */
.ws-btn-login,
.ws-btn-cart {
  position: relative;
  background: transparent;
  border: none;
  color: var(--ws-header-action-color);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ws-btn-login:hover,
.ws-btn-cart:hover {
  background: rgba(255, 255, 255, 0.15);
}

.ws-login {
  position: relative;
  display: flex;
  align-items: center;
}

.ws-login-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 140px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  padding: 4px;
  z-index: 20;
  display: flex;
  flex-direction: column;
}

.ws-login-menu[hidden] {
  display: none;
}

.ws-login-item {
  /* Reset so a <button> (Commerce7's in-chat Login action) matches the <a>
     (external Login/Register links) exactly. */
  display: block;
  /* width: 100%; */
  background: none;
  border: none;
  font-family: inherit;
  text-align: left;
  padding: 8px 12px;
  font-size: 14px;
  color: var(--ws-text);
  text-decoration: none;
  border-radius: 6px;
  cursor: pointer;
}

.ws-login-item:hover {
  background: var(--ws-surface);
}

.ws-login-item--empty {
  color: var(--ws-accent);
  cursor: default;
}

/* Old login-option-icon (script.js ~886): colored circle + first-letter
   initial, replacing the generic person icon once logged in. */
.ws-btn-avatar {
  display: grid;
  place-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--ws-selected-chip, var(--ws-primary));
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  text-transform: uppercase;
}

/* Old login-dropdown name row (script.js ~890-891) — non-interactive, just
   the customer's name above the Logout action. */
.ws-login-name {
  cursor: default;
  font-weight: 600;
}

.ws-login-name:hover {
  background: none;
}

.ws-login-divider {
  margin: 5px 0;
  border: none;
  height: 1px;
  background: #e5e5e5;
}

/* Parity: the count sits INSIDE the cart basket (small, same gold as the
   cart stroke), not a pill bubble — matches old #shop-cart-total-items. */
/* Count sits centered in the basket interior. The svg (19px) is centred in a
   32px button, so we centre on the basket body (~55% / 52%) rather than the
   old 19px-icon offsets, otherwise the number drops onto the wheels. */
/* Overlay the count on the cart icon. The badge box fills the button and
   flex-centres its text; bottom/left nudge it into the basket interior
   (values confirmed in-browser against the cart svg). */
.ws-cart-badge {
  position: absolute;
  inset: 0;
  bottom: 8px;
  left: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: currentColor;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  pointer-events: none;
}

.ws-cart-badge[hidden] {
  display: none;
}

/* Two stacked bands (old): crisp vineyard hero + gold promotion strip.
   Horizontal bleed is handled by .ws-scroll-top; keep margin 0 here so the
   banner does not add extra overflow. */
.ws-banner {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  margin: 0;
  max-width: 100%;
  overflow: hidden;
}

/* Hero wrapper — hosts optional main_banner_text overlay (old .chatbot-banner). */
.ws-banner-hero {
  position: relative;
  width: 100%;
}

/* Vineyard hero — full-width crisp image (old .chatbot-banner img, 110px). */
.ws-banner-img {
  width: 100%;
  height: 110px;
  object-fit: cover;
  display: block;
}

/* Old .chatbot-banner-text — overlay on the hero image when main_banner_text is set. */
.ws-banner-main-text {
  position: absolute;
  top: 50%;
  right: 10px;
  left: auto;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  pointer-events: none;
  z-index: 1;
  max-width: 90%;
  padding: 0;
  box-sizing: border-box;
}
.ws-banner-main-text span {
  display: block;
  font-style: normal;
  font-weight: 700;
  color: #ffffff;
  text-align: right;
  text-transform: uppercase;
  line-height: 90%;
  font-size: 20px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
}

/* Old .promotion: a ~60px gold strip with caption (left) + plain SHOP NOW
   (right). Text insets left 20px / right 37px, font-size 15px. Defaults to a
   warm gold gradient with a faint dotted texture (echoing the old strip image)
   when no banner_strip_image_url / banner_bg_image is configured. */
.ws-banner-promo {
  position: relative;
  min-height: 60px;
  padding: 2px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #ffffff;
  background-color: #b0a06b;
  /* Colour layer is themeable via banner_strip_color → --ws-banner-strip
     (init.js wraps the colour into a flat gradient). Unset → Fogarty gold.
     Dot pattern stays on top. Tenants with banner_bg_image set an inline
     background-image that replaces these layers. */
  background-image:
    radial-gradient(
      circle at center,
      rgba(255, 255, 255, 0.07) 1.2px,
      transparent 1.3px
    ),
    var(--ws-banner-strip, linear-gradient(135deg, #bcad79 0%, #a6945c 100%));
  background-size:
    14px 14px,
    cover;
  background-position: center, center;
  background-repeat: repeat, no-repeat;
  flex-shrink: 0;
}

.ws-banner-promo--image {
  background-size: cover;
}

/* Optional decorative image, supplied by tenant config. */
.ws-banner-curve {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 14px;
  background-repeat: no-repeat;
  background-position: bottom center;
  background-size: 100% 100%;
  pointer-events: none;
}

/* Old .promo-text-left: white, weight 400, max 62% width. */
.ws-banner-text {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.25;
  max-width: 62%;
}

/* Old .promo-text-right "SHOP NOW!": plain white bold uppercase text — no
   box/border. Centered, max 30% width. */
.ws-banner-cta {
  background: transparent;
  color: #ffffff;
  border: none;
  padding: 0;
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  text-align: center;
  cursor: pointer;
  max-width: 30%;
  flex-shrink: 0;
}

.ws-banner-cta:hover {
  background: transparent;
  opacity: 0.85;
}

.ws-promo-poster {
  flex-shrink: 0;
  overflow: hidden;
  display: block;
}

/* Old .chatbot-banner scenery photo: full-width, fixed 110px tall. */
.ws-promo-poster-img {
  width: 100%;
  display: block;
  object-fit: cover;
  height: 110px;
}

.ws-promo-poster.ws-clickable {
  cursor: pointer;
}

.ws-scroll-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  background: var(--ws-surface);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  position: relative;
  /* Match old #chat-scroll-wrapper — clip any child that overruns width. */
  overflow: hidden;
}

.ws-bg-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  pointer-events: none;
}

.ws-scroll-wrapper > .ws-messages,
.ws-scroll-wrapper > .ws-scrollbar {
  position: relative;
  z-index: 1;
}

.ws-messages {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  /* Match old #chatbot-container — prevent slight sideways drag on all agents. */
  overflow-x: hidden;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: var(--ws-message-gap);
  scrollbar-width: none;
}

.ws-messages::-webkit-scrollbar {
  display: none;
}

/* Relocated promo + banner (scroll with the conversation). Bleed past the
   .ws-messages padding so they stay full-width and flush to the header, like
   the old in-scroll banner; the first message keeps its normal gap below.
   Negative margin MUST match padding (6px) — larger values caused horizontal
   overflow across every agent. */
.ws-messages > .ws-scroll-top {
  margin: -6px -6px 0 -6px;
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: none;
  width: auto;
}

.ws-scrollbar {
  flex: 0 0 var(--ws-scrollbar-width);
  width: var(--ws-scrollbar-width);
  background: var(--ws-scrollbar-bg);
  border-left: 1px solid var(--ws-scrollbar-border);
  border-right: 1px solid var(--ws-scrollbar-border);
  border-radius: 12px;
  display: none;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding: 0;
  color: var(--ws-scrollbar-icon-color);
}

.ws-scroll-overflow .ws-scrollbar {
  display: flex;
}

.ws-scroll-btn {
  background: var(--ws-scrollbar-btn-bg);
  border: none;
  width: 100%;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: var(--ws-scrollbar-icon-color);
  padding: 0;
}

.ws-scroll-btn-jump {
  height: var(--ws-scrollbar-btn-jump-h);
}

.ws-scroll-btn-step {
  height: var(--ws-scrollbar-btn-step-h);
}

.ws-scroll-btn-top {
  margin-bottom: 1px;
}

.ws-scroll-btn-bottom {
  margin-top: 1px;
}

.ws-scroll-track {
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: center;
}

.ws-scroll-thumb {
  position: absolute;
  top: 0;
  width: 18px;
  min-height: var(--ws-scrollbar-thumb-min);
  background: var(--ws-scrollbar-thumb-bg);
  border-radius: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: grab;
  color: var(--ws-scrollbar-icon-color);
  user-select: none;
}

.ws-scroll-thumb:active {
  cursor: grabbing;
}

.ws-bot-row,
.ws-user-row {
  display: flex;
  align-items: flex-start;
  gap: var(--ws-avatar-gap);
}

.ws-bot-row {
  justify-content: flex-start;
  scroll-margin-top: 8px;
  scroll-margin-bottom: 8px;
}

.ws-user-row {
  justify-content: flex-end;
}

.ws-avatar {
  width: var(--ws-avatar-size);
  /* height: var(--ws-avatar-size); */
  /* border-radius: 50%; */
  background: var(--ws-avatar-bg);
  flex-shrink: 0;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ws-avatar img,
.ws-avatar svg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ws-input-area {
  flex-shrink: 0;
  padding: 12px 16px 0px;
  display: flex;
  gap: 12px;
  align-items: center;
  background: var(--ws-background);
}

.ws-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  /* border: 1px solid transparent; */
  border-radius: 6px;
  /* background: var(--ws-surface); */
  background: #ebebeb;
  padding: 4px 6px 4px 18px;
  min-height: 38px;
}

.ws-input-wrap:focus-within {
  border-color: var(--ws-primary);
}

.ws-input {
  flex: 1;
  border: none;
  padding: 8px 0;
  font-family: var(--ws-font) !important;
  font-size: 16px;
  outline: none;
  background: transparent;
  /* color: var(--ws-text);  */
  /* added by chintan Dobariya */
  color: #797264;
  font-weight: 600 !important;
}

.ws-input::placeholder {
  color: var(--ws-accent);
}

.ws-btn-mic {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  background: var(--ws-mic-bg);
  color: var(--ws-mic-color);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;
}

.ws-btn-mic:hover {
  opacity: 0.9;
}

.ws-btn-mic--listening {
  background: var(--ws-mic-active-bg);
  color: var(--ws-mic-active-color);
  animation: ws-mic-pulse 1.4s ease-in-out infinite;
}

@keyframes ws-mic-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(0, 0, 0, 0.08);
    transform: scale(1.05);
  }
}

.ws-btn-mic img,
.ws-btn-mic svg {
  width: 18px;
  height: 18px;
}

.ws-btn-send {
  flex-shrink: 0;
  background: transparent;
  color: var(--ws-primary);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  transition: opacity 0.2s ease;
}

.ws-btn-send:hover {
  opacity: 0.7;
}

.ws-btn-send svg {
  width: 32px;
  height: 32px;
}

.ws-footer {
  flex-shrink: 0;
  height: 40px;
  display: flex;
  align-items: center;
  /* justify-content: space-between; */
  gap: 12px;
  background: var(--ws-background);
  /* padding: 0 16px 8px; */
  padding: 0 16px;
}

.ws-footer-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ws-powered-text {
  font-size: 12px;
  color: var(--ws-accent);
}

.ws-powered-brand {
  font-size: 13px;
  font-weight: 400;
  color: var(--ws-text);
}

.ws-powered-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: inherit;
}

.ws-redaugment-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
}

.ws-powered-link:hover .ws-powered-brand {
  color: var(--ws-primary);
}

.ws-powered-logo {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.ws-footer-sep {
  width: 1px;
  height: 18px;
  background: var(--ws-border);
}

.ws-footer-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ws-feedback-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  margin: 0;
}

.ws-feedback-btn:hover .ws-feedback-label {
  color: var(--ws-primary);
}

.ws-feedback-label {
  font-size: 13px;
  color: var(--ws-text);
}

.ws-feedback-glasses {
  display: inline-flex;
  align-items: center;
  color: var(--ws-primary);
}

.ws-feedback-glasses svg {
  height: 22px;
  width: auto;
}

.ws-feedback-modal {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 16px;
}

.ws-feedback-dialog {
  background: var(--ws-background);
  border-radius: 12px;
  width: 100%;
  max-width: 360px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
}

.ws-fb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ws-fb-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-text);
}

.ws-fb-close {
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: var(--ws-accent);
  padding: 0 4px;
}

.ws-fb-desc {
  margin: 0;
  font-size: 13px;
  color: var(--ws-accent);
}

.ws-fb-rate-label {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  color: var(--ws-accent);
}

.ws-fb-glasses {
  display: flex;
  justify-content: center;
  gap: 28px;
  color: var(--ws-primary);
}

.ws-fb-glass {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s ease;
}

.ws-fb-glass:hover {
  transform: scale(1.05);
}

.ws-fb-glass-icon {
  display: inline-flex;
}

.ws-fb-glass-icon svg {
  width: 36px;
  height: 64px;
}

.ws-fb-glass-label {
  font-size: 13px;
  color: var(--ws-text);
  font-weight: 500;
}

.ws-fb-textarea {
  width: 100%;
  min-height: 80px;
  border: 1px solid var(--ws-border);
  border-radius: 8px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 13px;
  resize: vertical;
  outline: none;
  background: var(--ws-background);
  color: var(--ws-text);
  box-sizing: border-box;
}

.ws-fb-email {
  width: 100%;
  border: 1px solid var(--ws-border);
  border-radius: 8px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 13px;
  outline: none;
  background: var(--ws-background);
  color: var(--ws-text);
  box-sizing: border-box;
}

.ws-fb-textarea:focus,
.ws-fb-email:focus {
  border-color: var(--ws-primary);
}

.ws-fb-error {
  display: none;
  color: var(--ws-error);
  font-size: 11px;
}

.ws-fb-submit {
  background: var(--ws-primary);
  color: var(--ws-text-light);
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.ws-fb-submit:hover {
  opacity: 0.9;
}

.ws-fb-done {
  text-align: center;
  padding: 16px 0;
}

.ws-fb-done p {
  margin: 0;
  font-size: 15px;
  color: var(--ws-success);
  font-weight: 600;
}

.ws-fb-done small {
  display: block;
  margin-top: 4px;
  color: var(--ws-accent);
}

/* ⚠ SHARED/BASE — the bot/user bubble column, all tenants.
   A percentage cap (this was 85%) leaves dead space to the right of a long bot
   reply: 85% of the row, but the row already spent avatar+gap before the bubble
   starts, so the leftover never gets used. The old widget capped .message-content
   at 90-95%, which on a 450px panel is MORE than the space left after the 40px
   avatar column — i.e. in practice the bubble filled the row.
   Reproduced here without hard-coding an avatar width (variants change it):
   max-width:100% + min-width:0 lets flexbox shrink this item to exactly the
   space the avatar leaves. Short messages still hug their text — flex-basis is
   the content width, and shrinking only kicks in when it would overflow. */
.ws-bubble-wrap {
  position: relative;
  max-width: 100%;
  min-width: 0;
}

.ws-bubble-wrap > .ws-welcome,
.ws-bubble-wrap > .ws-bot-bubble,
.ws-bubble-wrap > .ws-user-bubble {
  max-width: 100%;
}

.ws-info-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #4a90e2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.ws-info-icon:hover {
  opacity: 0.85;
}

.ws-info-popover {
  position: absolute;
  /* Anchor directly under the info icon (top:8px, ~16px tall), not below the
     whole bubble — calc(100% ...) dropped it far below on tall welcome bubbles. */
  top: 30px;
  right: 8px;
  background: #ffffff;
  color: var(--ws-text);
  border: 1px solid var(--ws-border);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  max-width: 280px;
  z-index: 5;
}

/* Optional popover heading (welcome_info_title). Colour is themeable per winery
   via --ws-info-title, defaulting to the winery accent. */
.ws-info-popover-title {
  color: var(--ws-info-title, var(--ws-accent));
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 6px;
}

/* Body renders markdown (welcome_info_text). A blank line in the source becomes
   a double <br>, which gives the paragraph gap seen in the design. */

.ws-welcome,
.ws-bot-bubble {
  position: relative;
  border: 1px solid #1818181a;
}

.ws-welcome:has(.ws-info-icon),
.ws-bot-bubble:has(.ws-info-icon) {
  padding-right: 32px;
}

.ws-bot-bubble,
.ws-welcome {
  background: var(--ws-bot-bubble-bg);
  color: var(--ws-bot-bubble-color);
  padding: 8px;
  border-radius: var(--ws-bubble-radius);
  border-top-left-radius: var(--ws-bubble-tail-radius);
  line-height: 1.3em;
  font-size: 15px;
  word-wrap: break-word;
}

/* Markdown body (old .message-content) — marked.js emits p / h1–h3 / ul / hr. */
.ws-bot-bubble p,
.ws-welcome p {
  margin: 0;
  padding-bottom: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3em;
}

.ws-bot-bubble h1,
.ws-welcome h1 {
  margin: 0.5em 0 0 0.5em;
  font-size: 28px;
  font-weight: 400;
  line-height: 1.3em;
}

.ws-bot-bubble h2,
.ws-welcome h2 {
  margin: 0.5em 0 0 0.5em;
  font-size: 21px;
  font-weight: 400;
  line-height: 1.3em;
}

.ws-bot-bubble h3,
.ws-welcome h3 {
  margin: 0.5em 0 0 0.5em;
  padding-bottom: 0;
  font-size: 16.38px;
  font-weight: 400;
  line-height: 1.3em;
}

/* Goosecross #chatbot-container h3 strong */
.ws-bot-bubble h3 strong,
.ws-welcome h3 strong {
  color: #574d39;
  font-weight: 400;
  font-size: 15px;
  line-height: 19.07px;
}

.ws-bot-bubble h4,
.ws-bot-bubble h5,
.ws-bot-bubble h6,
.ws-welcome h4,
.ws-welcome h5,
.ws-welcome h6 {
  margin: 0.5em 0 0 0.5em;
  font-weight: 400;
  line-height: 1.3em;
}

.ws-bot-bubble a,
.ws-welcome a {
  color: #0000ee;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 400;
}

.ws-bot-bubble h3 a,
.ws-welcome h3 a {
  font-size: 16.38px;
}

.ws-bot-bubble p a,
.ws-welcome p a,
.ws-bot-bubble li a,
.ws-welcome li a {
  font-size: 15px;
}

/* Old .hr-after-h3 — divider under each ### heading (goosecross). */
.ws-bot-bubble .hr-after-h3,
.ws-welcome .hr-after-h3,
.ws-bot-bubble .note-divider,
.ws-welcome .note-divider {
  display: block;
  width: 100%;
  height: 0;
  margin: 7px 0;
  border: 0;
  border-top: 2px solid #a29b8d;
}

.ws-bot-bubble ul,
.ws-bot-bubble ol,
.ws-welcome ul,
.ws-welcome ol {
  margin: 0;
  padding: 0 0 0 5px;
  list-style: none;
}

.ws-bot-bubble li,
.ws-welcome li {
  margin-bottom: 5px;
}

.ws-bot-bubble ul > li,
.ws-bot-bubble ol > li,
.ws-welcome ul > li,
.ws-welcome ol > li {
  position: relative;
  padding-left: 1.3em;
  list-style-type: none;
}

.ws-bot-bubble ul > li::before,
.ws-bot-bubble ol > li::before,
.ws-welcome ul > li::before,
.ws-welcome ol > li::before {
  content: "✦";
  position: absolute;
  left: 0;
  color: #000;
  font-size: 1.2em;
}

.ws-bot-bubble ul ul > li::before,
.ws-bot-bubble ol ul > li::before,
.ws-bot-bubble ul ol > li::before,
.ws-bot-bubble ol ol > li::before,
.ws-welcome ul ul > li::before,
.ws-welcome ol ul > li::before,
.ws-welcome ul ol > li::before,
.ws-welcome ol ol > li::before {
  content: "⚬";
  color: #a29b8d;
  font-size: 1em;
}

.ws-bot-bubble ul ul > li,
.ws-bot-bubble ol ul > li,
.ws-bot-bubble ul ol > li,
.ws-bot-bubble ol ol > li,
.ws-welcome ul ul > li,
.ws-welcome ol ul > li,
.ws-welcome ul ol > li,
.ws-welcome ol ol > li {
  position: relative;
  padding-left: 1em;
}

.ws-bot-bubble li p,
.ws-welcome li p {
  display: inline;
  margin: 0;
}

/* Goosecross #chatbot-container ul/ol li strong — bold labels in diamond lists. */
.ws-bot-bubble li strong,
.ws-welcome li strong {
  font-weight: 700;
  font-size: 14px;
  line-height: 16.34px;
  color: #000;
}

.ws-user-bubble {
  background: var(--ws-user-bubble-bg-gradient);
  color: var(--ws-user-bubble-color);
  padding: 8px;
  border-radius: var(--ws-bubble-radius);
  border-top-right-radius: var(--ws-bubble-tail-radius);
  line-height: 1.45;
  font-size: 14px;
  word-wrap: break-word;
}

.ws-drawer-context-reply {
  position: relative;
  margin-left: auto;
  margin-bottom: 4px;
  max-width: 85%;
  cursor: pointer;
}

.ws-drawer-context-bubble {
  position: relative;
  background: color-mix(in srgb, var(--ws-primary, #6e1811) 15%, #f5f5f5);
  color: #333;
  font-size: 13px;
  line-height: 1.35;
  padding: 6px 12px;
  border-radius: 18px;
  transition: background 0.15s ease;
}

.ws-drawer-context-reply:hover .ws-drawer-context-bubble {
  background: color-mix(in srgb, var(--ws-primary, #6e1811) 25%, #f0f0f0);
}

.ws-drawer-context-bubble::after {
  content: "";
  position: absolute;
  bottom: -6px;
  right: 14px;
  width: 12px;
  height: 12px;
  background: inherit;
  border-radius: 50%;
}

.ws-drawer-context-bubble::before {
  content: "";
  position: absolute;
  bottom: -12px;
  right: 9px;
  width: 7px;
  height: 7px;
  background: inherit;
  border-radius: 50%;
}

.ws-drawer-context-bubble .ws-drawer-context-label {
  font-weight: 600;
  color: color-mix(in srgb, var(--ws-primary, #6e1811) 80%, #000);
}

.ws-carousel {
  position: relative;
  margin: 8px 0;
}

/* Side gutters so the prev/next arrows (pinned ~4px from the edge, 32px wide)
   sit BESIDE the cards instead of overlapping their titles. Applies to every
   winery's horizontal card carousels. Excludes the vertical reviews carousel
   (its nav is hidden and its cards are full-width). */
.ws-carousel:not(:has(.ws-rich-review-button)) {
  padding-left: 20px;
  padding-right: 20px;
}

/* Indent the vertical reviews carousel so its buttons line up under the
   avatar-offset bot bubble above it (36px = avatar width, .ws-bot-row). */
.ws-carousel:has(.ws-rich-review-button) {
  padding-left: 36px;
}

.ws-carousel-track {
  display: flex;
  align-items: stretch;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  /* proximity (not mandatory): mandatory + scroll-snap-align:start was eating
     the track's left padding on load, so the first feature card sat flush to
     the clip edge and looked cut off until the user dragged it. */
  scroll-snap-type: x proximity;
  scroll-padding-inline: 12px;
  /* Horizontal inset lives on first/last items below — padding alone is still
     collapsed by some engines when snapping the first card. */
  padding: 8px 0;
  scrollbar-width: none;
}

.ws-carousel-track::-webkit-scrollbar {
  display: none;
}

/* Fixed 240px card width (old .tasting_card / carousel parity). Scoped under
   .ws-carousel-track for higher specificity than .ws-card (width:100%), which
   otherwise wins by source order and stretches base cards (tasting/membership/
   event) full-width inside the carousel.
   Equal heights: stretch items + fill child so wine cards share one row height. */
.ws-carousel-track > .ws-carousel-item {
  flex: 0 0 auto;
  scroll-snap-align: start;
  width: 240px;
  display: flex;
  align-self: stretch;
}

/* Guaranteed left/right breathing room for the first & last feature card —
   mirrors old .carousel padding: 8px and keeps card glow/border fully visible
   (parent .ws-messages uses overflow-x: hidden). All wineries. Skips the
   vertical reviews carousel (full-width .ws-rich-review-button stack). */
.ws-carousel:not(:has(.ws-rich-review-button))
  .ws-carousel-track
  > .ws-carousel-item:first-child {
  margin-left: 12px;
}

.ws-carousel:not(:has(.ws-rich-review-button))
  .ws-carousel-track
  > .ws-carousel-item:last-child {
  margin-right: 12px;
}

.ws-carousel-track > .ws-carousel-item > * {
  /* width: 100%;
  height: 100%; */
  box-sizing: border-box;
}

.ws-carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #000;
  border: 1px solid var(--ws-border);
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  z-index: 2;
}

.ws-carousel-prev {
  left: 4px;
}

.ws-carousel-next {
  right: 4px;
}

.ws-carousel-nav:hover {
  /* background: #ffffff; */
}

/* ── Wine listing pagination (Shop Our Wines) ─────────────
   Shared across all wineries. Theme with --ws-pagination-*.
   Parity: old .wine-pagination / .wine-page-button (main.js). */
.ws-wine-listing {
  position: relative;
}

.ws-wine-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 4px 0 10px;
  padding: 8px 0;
  border-top: 1px solid var(--ws-pagination-border, rgba(0, 0, 0, 0.12));
  border-bottom: 1px solid var(--ws-pagination-border, rgba(0, 0, 0, 0.12));
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}

.ws-wine-pagination::-webkit-scrollbar {
  display: none;
}

.ws-wine-page-numbers {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ws-wine-page-button {
  appearance: none;
  -webkit-appearance: none;
  min-width: 32px;
  height: 32px;
  border: 1px solid var(--ws-pagination-btn-border, var(--ws-border));
  border-radius: 8px;
  background: var(--ws-pagination-btn-bg, #fff);
  color: var(--ws-pagination-btn-color, var(--ws-primary));
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-family: var(--ws-font);
}

.ws-wine-page-button.active {
  background: var(--ws-rich-cta-bg, var(--ws-primary));
  border-color: var(--ws-pagination-active-bg, var(--ws-primary));
  color: var(--ws-pagination-active-color, #fff);
}

.ws-wine-page-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.ws-wine-page-button.active:disabled {
  opacity: 1;
}

.ws-wine-page-arrow {
  font-size: 24px;
  font-weight: 400;
}

.ws-wine-pagination-loader {
  position: absolute;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  background: rgba(255, 255, 255, 0.78);
}

.ws-wine-page-spinner {
  width: 42px;
  height: 42px;
  border: 4px solid var(--ws-pagination-spinner-track, rgba(0, 0, 0, 0.15));
  border-top-color: var(--ws-pagination-spinner, var(--ws-primary));
  border-radius: 50%;
  animation: ws-wine-page-spin 0.8s linear infinite;
}

@keyframes ws-wine-page-spin {
  to {
    transform: rotate(360deg);
  }
}

.ws-wine-listing-loading .ws-carousel-track,
.ws-wine-listing-loading .ws-wine-pagination {
  pointer-events: none;
}

/* ── Feature cards (shared shell) ───────────────────────── */
.ws-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  /* border: 1px solid var(--ws-border); */
  border-radius: 8px;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  overflow: hidden;
  font-family: var(--ws-font);
}

.ws-card-head {
  position: relative;
}

.ws-card-img {
  display: block;
  width: 100%;
  /* aspect-ratio: 1 / 1; */
  object-fit: contain;
  background: var(--ws-surface);
}

.ws-card-soldout {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 2px 8px;
  background: #000000;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: 4px;
}

.ws-card-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1 auto;
  gap: 4px;
  padding: 10px 12px 12px;
}

.ws-card-title {
  margin: 0;
  font-family: var(--ws-card-title-font);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--ws-card-accent);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ws-card-sub {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--ws-text);
}

.ws-card-desc {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--ws-text);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ws-card-meta {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--ws-accent);
}

.ws-card-price {
  margin: 6px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-card-accent);
}

/* ⚠ SHARED/BASE — all tenants. cursor:pointer is explicit because the CTA can
   render as an <a> WITHOUT href (e.g. RESERVE NOW before a cta_link is set in
   the Feature Cards config); a hrefless <a> otherwise shows the default arrow. */
.ws-card-cta {
  margin-top: 10px;
  display: block;
  padding: 10px 14px;
  background: var(--ws-card-accent);
  color: #ffffff;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  /* text-transform: uppercase; */
  cursor: pointer;
}

.ws-card-cta:hover {
  opacity: 0.92;
}

/* ── Event detail card: wider slot, full description ────── */
.ws-card-divider {
  height: 1px;
  margin: 6px 0;
  background: rgba(0, 0, 0, 0.12);
}

.ws-card-desc-full {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  color: var(--ws-text);
}

.ws-carousel-item:has(.ws-event-details-card) {
  width: 300px;
}

/* ── Review platform links ──────────────────────────────── */
.ws-review-card {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border: 1px solid var(--ws-border);
  border-radius: 22px;
  background: #ffffff;
  color: var(--ws-card-accent);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
}

.ws-review-card:hover {
  background: var(--ws-surface);
}

/* Reviews are compact link pills, not fixed-width cards. ReviewCard.js
   returns the <a> itself as the carousel item (no wrapper div — .ws-carousel-item
   and .ws-rich-review-button land on the SAME element), so this must be a
   compound selector, not :has() (there's no separate descendant to match) —
   and the class is .ws-rich-review-button (ReviewCard.js), not .ws-review-card
   (unused leftover name — nothing in the codebase creates that class).
   align-self must be reset too: the base .ws-carousel-item rule sets
   align-self: stretch, and per the flexbox spec a stretched item with an
   auto cross-size still fills the container — width: auto alone doesn't
   stop it, so it needs its own flex-start to actually shrink to content. */
.ws-carousel-item.ws-rich-review-button {
  width: auto;
  align-self: flex-start;
}

/* ── Cart panel (non-carousel) ──────────────────────────── */
.ws-cart-panel {
  position: relative;
  margin: 8px 12px;
  padding: 12px;
  border: 1px solid var(--ws-border);
  border-radius: 8px;
  background: #ffffff;
  font-family: var(--ws-font);
}

.ws-cart-empty {
  margin: 0;
}

/* Mutation spinner overlay (parity with old .overlay-effect). */
.ws-cart-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.8);
}

.ws-cart-overlay::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  border: 4px solid #f5eded;
  border-top: 4px solid var(--ws-primary, #6e1811);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: ws-cart-spin 1s linear infinite;
}

@keyframes ws-cart-spin {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.ws-cart-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--ws-border);
  border-radius: 8px;
  margin-bottom: 10px;
}

.ws-cart-item-img {
  width: 56px;
  height: 72px;
  object-fit: contain;
  flex: 0 0 auto;
}

.ws-cart-item-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  width: 100%;
}

.ws-cart-item-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--ws-text);
}

.ws-cart-item-meta {
  margin: 0;
  font-size: 12px;
  color: #6b6b6b;
}

.ws-cart-item-price {
  margin: 4px 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ws-card-accent, #6e1811);
}

/* Cart-context Update: outlined + compact (old), not the solid wine-card button. */
.ws-cart-item-action {
  gap: 8px;
  margin-top: 4px;
}
/* Compact, fixed-width qty select so every cart row matches (overrides the
   flex:1 grow that .ws-fg-qty uses inside the narrow wine card). */

.ws-cart-item-action .ws-fg-qty {
  flex: 0 0 auto;
  width: 96px;
}

/* ── In-widget checkout flow: Ship/Pickup → form → order review ── */
/* CheckoutOptions uses .ws-bot-row + avatar + bubble (same as TextBubble). */

/* .ws-checkout-options.ws-bot-row {
  margin: 8px 12px;
  padding: 0;
  border: none;
  background: transparent;
} */

.ws-checkout-options .ws-checkout-chips {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.ws-order-review {
  margin: 8px 12px 8px 40px;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  border: 1px solid #f1f1f1;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(156, 85, 100, 0.1);
  font-family: var(--ws-font);
  width: auto;
  max-width: calc(100% - 52px);
  box-sizing: border-box;
}

.ws-order-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ws-order-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1d;
}

.ws-order-edit {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px 10px;
  border: 1px solid var(--ws-primary, #6e1811);
  border-radius: 2px;
  border: none;
  background: #fff;
  color: var(--ws-primary, #6e1811);
  font-family: var(--ws-font);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
}

.ws-order-edit svg {
  flex-shrink: 0;
}

.ws-order-divider {
  margin: 0;
  border: none;
  height: 1px;
  background: #e5e5e5;
  width: 100%;
}

.ws-order-details {
  font-size: 14px;
  display: flex;
  flex-direction: column;
  /* gap: 8px; */
}

.ws-order-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 14px;
  color: #0a0a0a;
}

.ws-order-row-label {
  min-width: 93px;
  font-weight: 600;
  color: #0a0a0a;
}

.ws-order-row-value {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}

.ws-order-summary-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e5e5;
}

.ws-order-summary-group:last-of-type {
  border-bottom: none;
  padding-bottom: 0;
}

.ws-order-group-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1d;
}

.ws-order-tender-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
  color: #0a0a0a;
}

.ws-order-item {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 8px;
  border: 1px solid rgba(142, 142, 142, 0.5);
  border-radius: 12px;
  background: #fff;
}

.ws-order-item-img-wrap {
  width: 80px;
  flex-shrink: 0;
}

.ws-order-item-img {
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: contain;
  display: block;
}

.ws-order-item-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ws-order-item-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ws-primary, #6e1811);
  line-height: 1.3;
}

.ws-order-item-desc {
  margin: 0;
  font-size: 13px;
  color: #0a0a0a;
  line-height: 1.3;
}

.ws-order-member-price {
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
}

.ws-order-strike {
  color: #737373;
}

.ws-order-item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}

.ws-order-item-qty {
  margin: 0;
  font-size: 13px;
  color: #0a0a0a;
}

.ws-order-item-price {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--ws-primary, #6e1811);
}

.ws-order-totals {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ws-order-totals-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

/* Old .summary-section-head — "Shipping" / "Taxes" above line-item rows */
.ws-order-summary-section-head {
  font-weight: 600;
  font-size: 15px;
  color: #0a0a0a;
  margin-block: 4px;
}

.ws-order-total-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 15px;
  font-weight: 400;
  color: #0a0a0a;
}

.ws-order-total-row > span:last-child {
  text-align: right;
  white-space: nowrap;
}

.ws-order-total-row.is-muted {
  color: #0a0a0a;
}

.ws-order-total-row.is-muted > span:last-child {
  color: #737373;
}

.ws-order-total-row.is-accent,
.ws-order-total-row.is-accent > span:last-child {
  color: #2e7d32;
}

.ws-order-estimated {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 15px;
  margin-block: 4px;
}

.ws-order-estimated > div:first-child {
  color: #737373;
  font-weight: 400;
}

.ws-order-estimated-hint {
  font-size: 12px;
  color: #737373;
  font-weight: 400;
}

.ws-order-estimated > div:last-child {
  color: #000;
  font-weight: 600;
  white-space: nowrap;
}

.ws-order-shipping-note {
  margin: 4px 0 0;
  color: #2e7d32;
  font-size: 12px;
  line-height: 1.4;
}

.ws-order-redirect {
  margin: 0;
  color: #737373;
  font-size: 14px;
}

.ws-order-footer {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ws-order-checkout-btn {
  width: 100%;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: var(--ws-primary, #6e1811);
  color: #fff;
  font-family: var(--ws-font);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ws-order-checkout-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

@media (max-width: 420px) {
  .ws-order-review {
    margin-left: 12px;
    max-width: calc(100% - 24px);
  }
}

/* legacy note class unused after redesign — keep harmless */
.ws-order-note {
  margin: 4px 0 8px;
  font-size: 12px;
  color: #c84e4e;
  font-weight: 600;
}

/* Checkout form — ss1 card layout (Contact + Delivery Address) */
.ws-checkout-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 12px 8px 40px;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  font-family: var(--ws-font);
  width: calc(100% - 52px);
  max-width: 100%;
  box-sizing: border-box;
}

.ws-checkout-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid #f1f1f1;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(156, 85, 100, 0.1);
  box-sizing: border-box;
}

.ws-checkout-section-head {
  margin: 0;
}

.ws-checkout-section-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1d;
  line-height: 1.2;
}

/* :not([hidden]) — an unconditional display here would win over the
   [hidden] attribute's UA display:none (author styles beat the UA
   stylesheet even at equal specificity), which is exactly why the payment
   step's new-card fields (CheckoutForm.js newCardWrap.hidden = true) still
   rendered visually alongside the saved-card picker instead of staying
   collapsed. Same fix as .ws-ship-address-others. */
.ws-checkout-fields:not([hidden]) {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.ws-checkout-row:not([hidden]) {
  display: grid;
  gap: 10px;
  align-items: start;
  width: 100%;
}

.ws-checkout-row--1 {
  grid-template-columns: 1fr;
}

.ws-checkout-row--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.ws-checkout-field {
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
}

.ws-checkout-field label {
  margin: 0;
  padding-bottom: 4px;
  color: #1d1d1d;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.2;
}

.ws-checkout-input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  font: inherit;
  font-size: 14px;
  color: #1d1d1d;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
}

.ws-checkout-input::placeholder {
  color: #9a9a9a;
}

.ws-checkout-input:focus {
  outline: none;
  border-color: var(--ws-primary, #9c5564);
}

.ws-checkout-error {
  min-height: 14px;
  margin-top: 2px;
  font-size: 12px;
  color: #c0392b;
  line-height: 1.2;
}

.ws-checkout-submit-error {
  display: block;
  padding: 0 4px;
  min-height: 0;
}

.ws-checkout-footer {
  /* margin-top: 6px;
  padding: 0 4px; */
}

.ws-checkout-submit {
  width: 100%;
  height: auto;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: var(--ws-primary, var(--ws-card-accent, #6e1811));
  color: #fff;
  font-family: var(--ws-font);
  font-size: 15px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
}

.ws-checkout-submit:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Stripe Elements mount points (ClubCheckoutForm.js "+ Add new card") reuse
   .ws-checkout-input's box/border/height — this just makes sure the iframe
   Stripe injects fills that box instead of using its own intrinsic size. */
.ws-club-stripe-el {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.ws-club-stripe-el iframe {
  display: block;
}

@media (max-width: 420px) {
  .ws-checkout-form {
    margin-left: 12px;
    width: calc(100% - 24px);
  }
  .ws-checkout-row--2 {
    grid-template-columns: 1fr;
  }
}

/* Saved shipping-address picker (ShipAddressOptions, kind
   "checkout-address-options") — shared by every Commerce7 tenant via
   features/optional/checkout_c7.js. */
.ws-ship-address-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
}

.ws-ship-address-radio {
  margin-top: 3px;
  flex-shrink: 0;
  accent-color: var(--ws-primary, var(--ws-card-accent, #6e1811));
}

.ws-ship-address-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

.ws-ship-address-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 2px;
  margin-left: auto;
  border: none;
  background: transparent;
  color: var(--ws-primary, var(--ws-card-accent, #6e1811));
  cursor: pointer;
}

.ws-ship-address-name {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1d;
}

.ws-ship-address-line {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #4a4a4a;
}

.ws-ship-address-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 2px;
  border: none;
  background: transparent;
  color: var(--ws-primary, var(--ws-card-accent, #6e1811));
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ws-ship-address-chevron {
  display: inline-flex;
  font-size: 18px;
  line-height: 1;
  transition: transform 0.15s ease;
}

.ws-ship-address-toggle.is-open .ws-ship-address-chevron {
  transform: rotate(90deg);
}

.ws-ship-address-others:not([hidden]) {
  display: flex;
  flex-direction: column;
}

.ws-ship-address-add {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 2px;
  margin-top: 4px;
  border: none;
  border-top: 1px solid #f1f1f1;
  background: transparent;
  color: #1d1d1d;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ws-ship-address-plus {
  font-size: 16px;
  line-height: 1;
}

.ws-ship-address-default-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.ws-ship-address-default-row span {
  font-size: 14px;
  color: #1d1d1d;
}

.ws-cart-item-action .ws-fg-update {
  flex: 0 0 auto;
  background: #fff;
  color: var(--ws-card-accent, #6e1811);
  border: 1px solid var(--ws-card-accent, #6e1811);
  font-weight: 600;
  text-transform: none;
  padding: 0 14px;
}

.ws-cart-summary {
  margin-top: 10px;
}

.ws-cart-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: var(--ws-text);
  padding: 2px 0;
}

.ws-cart-total {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--ws-border);
  font-weight: 700;
}

.ws-cart-checkout {
  margin-top: 12px;
}

/* ── User-message "Show More" toggle (parity: >50 words) ──────────── */
.ws-show-more {
  margin-left: 4px;
  color: blue;
  cursor: pointer;
}

/* ── Wine carousel footer: View cart / Proceed to checkout (parity) ── */
.ws-cart-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin: 8px 0;
  /* padding-left: 34px; */
}
/* Same accent as a selected/clicked prompt chip (.ws-prompt-chip.is-selected)
   — one consistent "active" brand color across the chat, not the separate
   rich-card accent token. */
.ws-cart-action-btn {
  /* flex: 1 1 auto; */
  padding: 6px 12px;
  border: 1px solid var(--ws-selected-chip, var(--ws-primary));
  border-radius: 6px;
  background: #fff;
  color: var(--ws-selected-chip, var(--ws-primary));
  font-family: var(--ws-font);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ws-cart-action-btn:hover {
  background: var(--ws-selected-chip, var(--ws-primary));
  color: #fff;
}

/* Selected (clicked) state — e.g. Ship/Pickup (CheckoutOptions): fills solid
   like a clicked prompt chip so the choice stays visibly marked. */
.ws-cart-action-btn.is-selected {
  background: var(--ws-selected-chip, var(--ws-primary));
  color: #fff;
}

.ws-cart-action-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.ws-cart-action-btn.is-selected:disabled {
  opacity: 1;
}

/* Contact connect options reuse the guided-prompt look (vertical stack, no
   icon). The "Email Us" <a> must not look like a link. */
.ws-contact-options .ws-prompt-chip {
  text-decoration: none;
}

/* Live-person (RedChirp) form: textarea + terms row. */
textarea.ws-checkout-input {
  height: auto;
  padding: 8px 10px;
}
.ws-liveperson-terms {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin: 4px 0 8px;
  font-size: 13px;
}
.ws-liveperson-terms input {
  margin-top: 3px;
  flex: 0 0 auto;
}
.ws-req {
  color: #ee0404;
}
.ws-liveperson-hint {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: #737373;
}
.ws-terms-head {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
}
.ws-terms-text {
  margin: 0;
  font-size: 12px;
  color: #555;
  line-height: 1.4;
}
.ws-terms-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ws-terms-toggle {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--ws-primary, #6e1811);
  cursor: pointer;
}
.ws-liveperson-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.ws-poweredby {
  margin: 0;
  font-size: 12px;
  color: #737373;
}

/* ── Tasting card layout (old .tasting_card / .tasting-inner) ──────
   8px shell padding insets the image; the card fills the equal-height
   carousel slot, and the learn-more + footer group is pinned to the
   bottom via margin:auto so price/BOOK-NOW always sit at the card foot. */
.ws-tasting-card {
  /* No explicit height: it would override the carousel's align-items:stretch
     (stretch only equalises auto-height items), leaving cards unequal. */
  padding: 8px;
}

.ws-tasting-card .ws-card-img {
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 6px;
}

.ws-tasting-card .ws-card-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* space-between fights Learn more alignment */
  padding: 12px 0 0;
  min-height: 0;
}

/* Pin Learn more + footer as one bottom group (old equal-height carousel).
   Only Learn more gets margin-top:auto — footer stays immediately under it. */
.ws-tasting-card .ws-card-learnmore {
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 0;
}

.ws-tasting-card .ws-tasting-footer {
  margin-top: 10px;
}

/* Event card: pin the learn-more + price + CTA footer to the bottom so the
   button row lines up across cards of differing content height (old
   .event-footer sat at the bottom of .event-inner). The carousel stretches
   cards to equal height and .ws-card-body already fills the card, so pushing
   the first footer element down is all that's needed — same as tasting above. */
.ws-event-card .ws-card-learnmore {
  margin-top: auto;
}

/* ── Tasting card: learn-more + price/CTA footer ────────── */
.ws-card-learnmore {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  /* width: 100%; */
  margin: 6px auto 0;
  padding: 9px 12px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 20px;
  background: #ffffff;
  color: var(--ws-card-accent);
  font-family: var(--ws-font);
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  cursor: pointer;
}

.ws-card-learnmore:hover {
  background: var(--ws-surface);
}

.ws-learnmore-icon {
  display: inline-flex;
  align-items: center;
}

.ws-tasting-footer,
.ws-event-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
}

.ws-price-block {
  display: flex;
  flex-direction: column;
}

.ws-card-unit {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--ws-text);
}

.ws-tasting-footer .ws-card-price,
.ws-event-footer .ws-card-price {
  margin: 0;
}

.ws-tasting-footer .ws-card-cta,
.ws-event-footer .ws-card-cta {
  margin-top: 0;
  width: auto;
  white-space: nowrap;
}

/* ── Wine-club membership: wine-selection carousel (common C7 feature) ────
   Card layout parity with old createWineClubSelection (main.js 2531) /
   .club-selection-card (index.css ~4903): image + "Tasting notes" pill,
   title, a centered qty stepper, then a centered price row (struck-through
   regular price + bold member price) and a running total. */
.ws-club-selection {
  margin: 8px 12px 8px 40px;
}

.ws-club-selection-card
  > *:not(.ws-club-selection-head):not(.ws-club-selection-tasting) {
  padding: 0 12px;
}

/* Fixed-height, centered image box — same wrapping as the "Shop our wines"
   wine-listing card's own head (WineCard.js's *-winecard-head/-img). */
.ws-club-selection-head {
  position: relative;
  height: 180px;
  min-height: 180px;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  margin-bottom: 12px;
}

.ws-club-selection-head .ws-club-selection-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

/* Big centered pill, same shape/size as the wine-listing card's own Tasting
   notes button (WineCard.js ws-*-winecard-tasting) — var(--ws-card-accent)
   is the shared default; tenants that pin their accent to a different token
   (e.g. Valle della Pace's --ws-secondary) override this in their own
   styles.css, same as they already do for the wine-listing card. */
.ws-club-selection-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: 8px auto 0;
  border: 1px solid var(--ws-card-accent);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(0, 0, 0, 0.16);
  color: var(--ws-card-accent);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}

.ws-club-selection-card .ws-club-selection-title {
  margin-top: 8px;
  min-height: 48px;
  line-height: normal;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ws-club-qty-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 9px 0 0;
}

.ws-club-qty-stepper button {
  width: 36px;
  height: 36px;
  padding: 4px;
  border: 1px solid #b7b5b5;
  border-radius: 4px;
  background: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.ws-club-qty-value {
  min-width: 40px;
  text-align: center;
  font-weight: 600;
}

.ws-club-selection-card-footer {
  width: 100%;
  text-align: center;
  font-weight: 600;
}

.ws-club-selection-prices {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin: 8px 0 7px;
}

.ws-club-selection-prices .ws-order-strike {
  font-size: 14px;
  font-weight: 400;
}

.ws-club-selection-member-price {
  font-size: 18px;
  font-weight: 600;
  color: var(--ws-card-accent);
}

.ws-club-selection-total {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: var(--ws-card-accent);
}

.ws-club-selection-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 12px 0;
  flex-wrap: wrap;
}

.ws-club-selection-counter {
  font-size: 13px;
  color: var(--ws-text);
}

/* Valle wine card subtitle — old .wine_card_vineyard "Members receive 10-20% off"
   (index.css 2468: 0.75rem, no bottom margin, black). */
.ws-vp-memberline {
  margin: 2px 0 0;
  font-size: 0.75rem;
  color: var(--ws-text, #000);
}

/* Valle add-to-cart is full width (old .add-to-cart width:100%). */
.ws-vp-add {
  width: 100%;
}

/* ── Valle Commerce7 in-chat login form (old showLoginForm) ────────────── */
.ws-vp-login {
  margin: 8px 10px 10px;
  padding: 16px 24px;
  background: #ffffff;
  border: 1px solid #f1f1f1;
  border-radius: 12px;
  box-shadow: 0 0 10px rgba(156, 85, 100, 0.1);
  position: relative;
}
.ws-vp-login-head {
  margin-bottom: 12px;
}
.ws-vp-login-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--ws-text, #1d1d1d);
}
.ws-vp-login-desc {
  font-size: 13px;
  color: var(--ws-text-muted, #6b6b6b);
  margin-top: 2px;
}
.ws-vp-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
.ws-vp-field label {
  font-size: 13px;
  font-weight: 600;
  padding-bottom: 7px;
  color: var(--ws-text, #1d1d1d);
}
.ws-vp-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  outline-color: var(--ws-primary);
}
.ws-vp-pwd-row {
  position: relative;
  display: flex;
  align-items: center;
}
.ws-vp-pwd-row .ws-vp-input {
  padding-right: 38px;
}
.ws-vp-eye {
  position: absolute;
  right: 10px;
  display: inline-flex;
  cursor: pointer;
  color: var(--ws-text-muted, #6b6b6b);
}
.ws-vp-field-error,
.ws-vp-form-error {
  display: block;
  color: #dc2626;
  font-size: 13px;
  margin-top: 2px;
}
.ws-vp-login-footer {
  display: flex;
  flex-direction: column;
}
.ws-vp-login-submit {
  width: 100%;
  padding: 10px 12px;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 400;
  color: #fff;
  background: var(--ws-primary);
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.ws-vp-login-submit:hover {
  opacity: 0.92;
}
.ws-vp-login-submit:disabled {
  opacity: 0.6;
  cursor: default;
}
.ws-vp-login-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  vertical-align: middle;
  animation: ws-vp-login-spin 0.7s linear infinite;
}
@keyframes ws-vp-login-spin {
  to {
    transform: rotate(360deg);
  }
}
.ws-vp-login-hint {
  font-size: 13px;
  text-align: center;
  margin: 10px 0 0;
  color: var(--ws-text-muted, #6b6b6b);
}
.ws-vp-login-hint a {
  color: var(--ws-primary);
  font-weight: 600;
}
.ws-vp-login-skip {
  font-size: 13px;
  text-align: center;
  margin: 8px 0 0;
  color: var(--ws-primary);
  cursor: pointer;
  text-decoration: underline;
}

/* ── Valle Commerce7 checkout steps (delivery / ship / pickup / payment) ── */
.ws-vp-checkout {
  margin: 8px 10px 10px;
  padding: 16px 24px;
  background: #ffffff;
  border: 1px solid #f1f1f1;
  border-radius: 12px;
  box-shadow: 0 0 10px rgba(156, 85, 100, 0.1);
}
.ws-vp-checkout-title {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 700;
  color: var(--ws-text, #1d1d1d);
}
.ws-vp-checkout-actions {
  display: flex;
  gap: 8px;
}
.ws-vp-checkout-actions .ws-vp-login-submit {
  margin-top: 0;
}
/* Pickup location address (from the C7 pickup-location data). */
.ws-vp-pickup-address {
  margin: -4px 0 10px;
  font-size: 12px;
  line-height: 1.4;
  color: #574d39;
}
.ws-vp-secondary {
  color: #7a0214;
  background: transparent;
  border: 1px solid #7a0214;
}
.ws-vp-secondary:hover {
  background: rgba(122, 2, 20, 0.06);
  opacity: 1;
}
.ws-vp-saved-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}
.ws-vp-saved-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--ws-border, #d4d4d4);
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
}
.ws-vp-newcard[hidden] {
  display: none;
}
.ws-vp-order-confirm {
  margin: 8px 10px 10px;
  padding: 16px 24px;
  background: #ffffff;
  border: 1px solid #f1f1f1;
  border-radius: 12px;
  box-shadow: 0 0 10px rgba(156, 85, 100, 0.1);
}
.ws-vp-order-confirm-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  color: #65a30d;
}
.ws-vp-order-confirm-body {
  margin: 0;
  font-size: 14px;
  color: var(--ws-text, #1d1d1d);
}

/* ── Typing indicator (transient bot bubble) ────────────── */
.ws-typing {
  display: inline-flex;
  align-items: center;
}

/* Three-dot loader ported from old styles.css .loader / @keyframes l1. */
.ws-typing-dots {
  display: inline-block;
  width: 30px;
  aspect-ratio: 4;
  background: radial-gradient(
      circle closest-side,
      var(--ws-accent) 90%,
      transparent
    )
    0 / 33.333% 100% space;
  clip-path: inset(0 100% 0 0);
  animation: ws-typing-l1 1s steps(4) infinite;
}

@keyframes ws-typing-l1 {
  to {
    clip-path: inset(0 -34% 0 0);
  }
}

.ws-prompts {
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* chips shrink to their text instead of stretching */
  gap: 8px;
  box-sizing: border-box;
  max-width: 100%;
  /* Indent the chips to line up with the chat bubble (to the right of the bot
     avatar), so they read as a message — not starting under the avatar. Shared
     by every winery; = avatar width + avatar↔bubble gap. */
  padding: 8px 12px 12px calc(var(--ws-avatar-size) + var(--ws-avatar-gap));
}

/* `.ws-prompts--recommendations` (follow-up chips from get_question_recommendations)
   is a STRUCTURAL HOOK ONLY — deliberately unstyled here. The old widget split
   these two ways: goosecross/lightningrock/potters/valledellapace gave them
   `.same-opt-btn { width:100% }` (one shared width), halleck/hestan left them
   shrink-to-text like the guided buttons. So a tenant opts in from its variant
   styles.css; the base default stays shrink-to-text for everyone else. */

.ws-prompt-chip {
  display: flex;
  align-items: start;
  gap: 10px;
  padding: 8px 12px;
  box-sizing: border-box;
  max-width: 100%;
  background: #fff;
  border: 1px solid var(--ws-border);
  border-radius: 8px;
  color: var(--ws-text);
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.15s,
    border-color 0.15s;
}

.ws-prompt-chip:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: var(--ws-primary);
}

/* Selected (clicked) guided chip — fills with the winery's brand color, like
   the old widget's changeBKG(). BASE so EVERY tenant gets it (including new
   wineries with no variant folder, e.g. pottersvineyard); a variant's
   styles.css can override with its exact brand colors. Placed after :hover so
   it wins at equal specificity. */
.ws-prompt-chip.is-selected {
  background: var(--ws-selected-chip, var(--ws-primary));
  border-color: var(--ws-selected-chip, var(--ws-primary));
  color: #ffffff;
}
.ws-prompt-chip.is-selected .ws-prompt-icon {
  color: inherit;
}

.ws-prompt-icon {
  display: inline-flex;
  align-items: center;
  /* color: var(--ws-primary); */
  flex-shrink: 0;
}

.ws-prompt-label {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

/* ──────────────────────────────────────────────────────────────
   Mobile responsive (≤480px — portrait phones)
   The widget lives in a Shadow DOM, so these rules can't leak to the
   host site or be overridden by it. This block is LAST in the file so
   it wins over the base panel/button rules at equal specificity.

   - The base panel is a fixed 450px box, which overflows a ~375–430px
     phone viewport (it's pinned right, so it spills off the left).
     On phones it goes full-screen instead.
   - Heights use dynamic viewport units (dvh) with a vh fallback so the
     mobile browser chrome / on-screen keyboard don't hide the input.
   - Touch targets grow to 44px (icons were 32px).
   - The input goes to 16px so iOS doesn't auto-zoom on focus
     (--ws-font-size is 14px).
   The right-edge launcher tab (#ws-launcher) already works on mobile.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  #ws-chat-panel {
    top: 0;
    bottom: auto;
    left: 0;
    right: 0;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
  }

  /* .ws-btn-refresh,
  .ws-btn-minimize,
  .ws-btn-close,
  .ws-btn-login,
  .ws-btn-cart {
    width: 44px;
    height: 44px;
  } */

  .ws-input {
    font-size: 16px;
  }
}

.ws-cp-qty-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 0;
}

.ws-cp-delete,
.ws-cp-dec,
.ws-cp-inc {
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  min-width: 30px;
  max-width: 30px;
  height: 30px;
  min-height: 30px;
  padding: 0 !important;
  border-radius: 2px;
  border: 1px solid #e5e5e5;
  background: #f1f1f1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #0a0a0a;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-transform: none;
}
